import { type AnyRoute, redirect } from "@tanstack/react-router";
import { omit } from "remeda";
import * as z from "zod/mini";

type SearchRecord = Record<string, unknown>;
type SearchSchema = z.ZodMiniType<SearchRecord>;

const isSchema = (value: unknown): value is SearchSchema =>
  typeof value === "object" && value !== null && typeof (value as SearchSchema).safeParse === "function";

const offendingKey = (issue: z.core.$ZodIssue): string | undefined => {
  const segment = issue.path[0];
  return segment == null || typeof segment === "symbol" ? undefined : String(segment);
};

const isNumeric = (value: string): boolean => value.trim() !== "" && !Number.isNaN(Number(value));

const coerceScalar = (value: string): string | number | boolean => {
  if (isNumeric(value)) return Number(value);
  if (value === "true" || value === "false") return value === "true";
  return value;
};

// TanStack's JSON parser types params where react-router passed every param as a string, so a URL can
// carry a shape the schema rejects. Try coercions back to the schema's shape, each kept only if it validates.
const candidatesFor = (value: unknown): Array<unknown> => {
  if (typeof value === "string") {
    const out: Array<unknown> = [];
    const scalar = coerceScalar(value);
    if (scalar !== value) out.push(scalar);
    if (value.includes(",")) {
      const parts = value.split(",");
      out.push(parts.map(coerceScalar), parts);
    }
    out.push([scalar]);
    return out;
  }
  if (typeof value === "number" || typeof value === "boolean") return [String(value), [value]];
  return [];
};

const firstOffendingKey = (input: SearchRecord, issues: ReadonlyArray<z.core.$ZodIssue>): string | undefined =>
  issues.map(offendingKey).find((key): key is string => key !== undefined && key in input);

// Set the dropped key to undefined, not delete it: TanStack merges `{ ...parentSearch, ...validated }`,
// so a deleted key resurrects from the raw parent search and still reaches useSearch()/the API.
const resolveKey = (schema: SearchSchema, input: SearchRecord, key: string): SearchRecord => {
  for (const candidate of candidatesFor(input[key])) {
    const trial = { ...input, [key]: candidate };
    const result = schema.safeParse(trial);
    if (result.success || !result.error.issues.some((issue) => offendingKey(issue) === key)) return trial;
  }
  return { ...input, [key]: undefined };
};

type Recovery = { value: SearchRecord; dropped: ReadonlyArray<string> };

const recover = (schema: SearchSchema, search: SearchRecord): Recovery => {
  let input = search;
  const dropped: Array<string> = [];
  for (let attempt = 0; attempt <= Object.keys(search).length; attempt++) {
    const result = schema.safeParse(input);
    if (result.success) return { value: result.data, dropped };
    const key = firstOffendingKey(input, result.error.issues);
    if (key === undefined) return { value: {}, dropped };
    input = resolveKey(schema, input, key);
    if (input[key] === undefined && !dropped.includes(key)) dropped.push(key);
  }
  return { value: {}, dropped };
};

// Recover malformed search params instead of throwing SearchParamError, which TanStack surfaces as a
// full-app crash above the error boundary. A whole-object failure that names no field yields empty search.
export const resilientValidateSearch =
  (schema: SearchSchema) =>
  (search: SearchRecord): SearchRecord =>
    recover(schema, search).value;

export const hardenSearchValidation = (route: AnyRoute): void => {
  const { validateSearch, beforeLoad } = route.options;
  if (isSchema(validateSearch)) {
    route.options.validateSearch = resilientValidateSearch(validateSearch);
    // validateSearch can't touch the URL, so redirect to strip a dropped param from the address bar too.
    // Boolean-validate first: clean search (the common case) skips recover's full parse + issue walk.
    route.options.beforeLoad = (ctx: { location: { pathname: string; search: SearchRecord } }) => {
      if (!z.validate(validateSearch, ctx.location.search)) {
        const { dropped } = recover(validateSearch, ctx.location.search);
        if (dropped.some((key) => key in ctx.location.search)) {
          throw redirect({
            to: ctx.location.pathname,
            search: omit(ctx.location.search, dropped),
            replace: true,
          });
        }
      }
      return beforeLoad?.(ctx);
    };
  }
  for (const child of route.children ?? []) hardenSearchValidation(child);
};
